import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, UploadSimple, FilePdf, ImageSquare, Sparkle, Trash, Download,
  ListBullets, X, Plus, Files, CaretDown, CaretRight, Check, CurrencyDollar,
  House, NotePencil, Calendar, ArrowsClockwise, FloppyDisk, FolderSimple, FolderPlus,
  ArrowSquareOut, FolderOpen
} from "@phosphor-icons/react";

const CATEGORIES = ["cabinet", "millwork", "hardware", "linear_footage", "material"];
const UNITS = ["ea", "lf", "sf", "bf", "pr"];
const CATEGORY_LABEL = {
  cabinet: "Cabinetry",
  millwork: "Millwork",
  hardware: "Hardware",
  linear_footage: "Linear Footage",
  material: "Materials",
};
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previews, setPreviews] = useState({}); // file_id -> blob url
  const [pageSelections, setPageSelections] = useState({}); // file_id -> Set<number>
  const [expandedFiles, setExpandedFiles] = useState({}); // file_id -> bool
  const [collapsedRooms, setCollapsedRooms] = useState({}); // room -> true if collapsed
  const [collapsedCats, setCollapsedCats] = useState({}); // "room|cat" -> true if collapsed
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [contextDraft, setContextDraft] = useState("");
  const [quoteDateDraft, setQuoteDateDraft] = useState("");
  const [appendMode, setAppendMode] = useState(false);
  const [savedEstimates, setSavedEstimates] = useState([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveDraft, setSaveDraft] = useState({ name: "", folder: "", notes: "" });
  const [folderCollapsed, setFolderCollapsed] = useState({}); // folder path -> bool
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const pollAttemptsRef = useRef(0);

  // Cleanup polling on unmount
  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const refresh = async () => {
    const r = await api.get(`/projects/${id}`);
    setProject(r.data);
    setPageSelections((prev) => {
      const next = { ...prev };
      for (const d of r.data.drawings || []) {
        if (!next[d.file_id]) {
          if (d.content_type === "application/pdf" && d.page_count > 0) {
            next[d.file_id] = new Set(Array.from({ length: d.page_count }, (_, i) => i + 1));
          } else {
            next[d.file_id] = new Set([1]);
          }
        }
      }
      return next;
    });
    try {
      const er = await api.get(`/projects/${id}/estimate`);
      setEstimate(er.data);
    } catch {}
    if (r.data.status === "analyzing" && !pollRef.current) {
      setAnalyzing(true);
      startPolling();
    }
  };

  useEffect(() => {
    refresh();
    return () => {
      Object.values(previews).forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line
  }, [id]);

  // Lazy-load drawing previews
  useEffect(() => {
    if (!project) return;
    project.drawings.forEach(async (d) => {
      if (previews[d.file_id]) return;
      try {
        const r = await api.get(`/files/${d.file_id}/preview`, { responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        setPreviews((p) => ({ ...p, [d.file_id]: url }));
      } catch {}
    });
    // eslint-disable-next-line
  }, [project]);

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/projects/${id}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${files.length} file${files.length > 1 ? "s" : ""} uploaded`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleUpload(Array.from(e.dataTransfer.files || []));
  };

  const removeDrawing = async (fileId) => {
    try {
      await api.delete(`/projects/${id}/drawings/${fileId}`);
      setPreviews((p) => { const c = { ...p }; if (c[fileId]) URL.revokeObjectURL(c[fileId]); delete c[fileId]; return c; });
      setPageSelections((p) => { const c = { ...p }; delete c[fileId]; return c; });
      setExpandedFiles((e) => { const c = { ...e }; delete c[fileId]; return c; });
      await refresh();
    } catch {
      toast.error("Failed to remove file");
    }
  };

  const togglePage = (fileId, pageNum) => {
    setPageSelections((prev) => {
      const cur = new Set(prev[fileId] || []);
      if (cur.has(pageNum)) cur.delete(pageNum);
      else cur.add(pageNum);
      return { ...prev, [fileId]: cur };
    });
  };

  const setAllPages = (fileId, pageCount, selectAll) => {
    setPageSelections((prev) => ({
      ...prev,
      [fileId]: selectAll ? new Set(Array.from({ length: pageCount }, (_, i) => i + 1)) : new Set(),
    }));
  };

  const totalSelectedPages = useMemo(() => {
    if (!project) return 0;
    return (project.drawings || []).reduce((s, d) => s + (pageSelections[d.file_id]?.size || 0), 0);
  }, [project, pageSelections]);

  // Estimated AI cost: ~$0.55/page for typical cabinetry shop drawings (Claude Sonnet 4.5 vision + structured JSON)
  // Real-world observed range: $0.40–$0.75 per page depending on drawing density.
  const PER_PAGE_COST = 0.55;
  const PER_CALL_CAP = 2.00; // Emergent Universal Key per-call budget cap
  const estimatedCost = useMemo(() => {
    if (totalSelectedPages === 0) return { low: 0, mid: 0, high: 0 };
    return {
      low: totalSelectedPages * 0.40,
      mid: totalSelectedPages * PER_PAGE_COST,
      high: totalSelectedPages * 0.75,
    };
  }, [totalSelectedPages]);
  const willExceedCap = estimatedCost.high > PER_CALL_CAP;

  const openRunDialog = () => {
    if (!project?.drawings?.length) return toast.error("Upload at least one drawing");
    if (totalSelectedPages === 0) return toast.error("Select at least one page to analyze");
    setContextDraft(project.context || "");
    setQuoteDateDraft(project.quote_date || new Date().toISOString().slice(0, 10));
    // Default to append mode if there are already line items (so split-batches workflow accumulates)
    setAppendMode((project.line_items || []).length > 0);
    setRunDialogOpen(true);
  };

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollAttemptsRef.current = 0;
    pollRef.current = setInterval(async () => {
      pollAttemptsRef.current += 1;
      try {
        const r = await api.get(`/projects/${id}`);
        const p = r.data;
        setProject(p);
        if (p.status === "ready") {
          clearInterval(pollRef.current); pollRef.current = null;
          setAnalyzing(false);
          try {
            const er = await api.get(`/projects/${id}/estimate`);
            setEstimate(er.data);
          } catch {}
          toast.success(`Found ${(p.line_items || []).length} line items`);
        } else if (p.status === "error") {
          clearInterval(pollRef.current); pollRef.current = null;
          setAnalyzing(false);
          toast.error(p.analyze_error || "Analysis failed", { duration: 10000 });
          await api.put(`/projects/${id}`, { status: "draft" });
        } else if (pollAttemptsRef.current > 120) {
          clearInterval(pollRef.current); pollRef.current = null;
          setAnalyzing(false);
          toast.error("Analysis is taking too long. Refresh the page to check status.", { duration: 10000 });
        }
      } catch {
        if (pollAttemptsRef.current > 120) {
          clearInterval(pollRef.current); pollRef.current = null;
          setAnalyzing(false);
        }
      }
    }, 3000);
  };

  const confirmRunAnalyze = async (forceCancel = false) => {
    setRunDialogOpen(false);
    setAnalyzing(true);
    toast.info(`Reading ${totalSelectedPages} page${totalSelectedPages !== 1 ? "s" : ""}… 30–120 seconds.`, { duration: 6000 });
    try {
      if (forceCancel) {
        try { await api.post(`/projects/${id}/cancel-analyze`); } catch {}
      }
      const payload = {
        page_selections: Object.fromEntries(
          Object.entries(pageSelections).map(([fid, set]) => [fid, Array.from(set).sort((a, b) => a - b)])
        ),
        context: contextDraft,
        quote_date: quoteDateDraft,
        mode: appendMode ? "append" : "replace",
      };
      const r = await api.post(`/projects/${id}/analyze`, payload);
      // Kickoff returns a minimal { project_id, status, message } payload — don't overwrite the full project doc.
      // Just update the status so the UI shows "analyzing" and let polling refresh the rest.
      setProject((prev) => prev ? { ...prev, status: r.data?.status || "analyzing", analyze_error: null } : prev);
      startPolling();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Failed to start analysis";
      console.error("Analyze start failed:", status, e?.response?.data);
      setAnalyzing(false);

      if (status === 409) {
        // Offer to force-cancel and retry
        toast.error("Analysis is already running.", {
          duration: 12000,
          action: {
            label: "Reset & retry",
            onClick: async () => {
              try {
                await api.post(`/projects/${id}/cancel-analyze`);
                toast.success("Reset. Retrying…");
                confirmRunAnalyze(false);
              } catch {
                toast.error("Reset failed");
              }
            },
          },
        });
      } else {
        toast.error(detail, { duration: 10000 });
      }
    }
  };

  const updateItem = (lineId, patch) => {
    setProject((p) => ({
      ...p,
      line_items: (p.line_items || []).map((li) => (li.id === lineId ? { ...li, ...patch } : li)),
    }));
  };

  const addBlankItem = (room = "General") => {
    setProject((p) => ({
      ...p,
      line_items: [
        ...(p.line_items || []),
        {
          id: crypto.randomUUID(),
          room,
          category: "cabinet", name: "New item", description: "",
          quantity: 1, unit: "ea", width_in: null, height_in: null, depth_in: null,
          material: "", finish: "", unit_cost: 0, labor_hours: 0, notes: "",
        },
      ],
    }));
  };

  // Live-computed estimate from current line_items + saved rates.
  // Falls back to backend-computed estimate values when present.
  const liveEstimate = useMemo(() => {
    const items = project?.line_items || [];
    const labor_rate = Number(estimate?.labor_rate) || 75;
    const overhead_pct = Number(estimate?.overhead_pct) || 15;
    const margin_pct = Number(estimate?.margin_pct) || 25;
    const tax_pct = Number(estimate?.tax_pct) || 0;

    const materials_cost = items.reduce((s, x) => s + (Number(x.unit_cost) || 0) * (Number(x.quantity) || 0), 0);
    const labor_hours = items.reduce((s, x) => s + (Number(x.labor_hours) || 0), 0);
    const labor_cost = labor_hours * labor_rate;
    const direct = materials_cost + labor_cost;
    const overhead = direct * (overhead_pct / 100);
    const sub_with_oh = direct + overhead;
    const margin = sub_with_oh * (margin_pct / 100);
    const subtotal = sub_with_oh + margin;
    const tax = subtotal * (tax_pct / 100);
    const grand_total = subtotal + tax;

    return {
      materials_cost: round2(materials_cost),
      labor_hours: round2(labor_hours),
      labor_rate,
      labor_cost: round2(labor_cost),
      direct_cost: round2(direct),
      overhead_pct,
      overhead: round2(overhead),
      margin_pct,
      margin: round2(margin),
      subtotal: round2(subtotal),
      tax_pct,
      tax: round2(tax),
      grand_total: round2(grand_total),
    };
  }, [project, estimate]);

  const removeItem = (lineId) => {
    setProject((p) => ({
      ...p,
      line_items: (p.line_items || []).filter((li) => li.id !== lineId),
    }));
  };

  const refreshTotals = async () => {
    setSaving(true);
    try {
      // Persist current line_items (so PDF export is up-to-date) and refetch the authoritative estimate
      await api.put(`/projects/${id}`, { line_items: project.line_items });
      const er = await api.get(`/projects/${id}/estimate`);
      setEstimate(er.data);
      toast.success("Totals refreshed");
    } catch {
      toast.error("Refresh failed");
    } finally {
      setSaving(false);
    }
  };

  const loadSavedEstimates = async () => {
    try {
      const r = await api.get(`/projects/${id}/saved-estimates`);
      setSavedEstimates(r.data || []);
    } catch {}
  };

  useEffect(() => { if (id) loadSavedEstimates(); /* eslint-disable-next-line */ }, [id]);

  const openSaveDialog = () => {
    setSaveDraft({
      name: project?.name ? `${project.name} — ${new Date().toLocaleDateString()}` : "Untitled Estimate",
      folder: "",
      notes: "",
    });
    setSaveDialogOpen(true);
  };

  const performSave = async (startNew) => {
    if (!saveDraft.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      // Persist current line items first so the snapshot is up-to-date
      await api.put(`/projects/${id}`, { line_items: project.line_items });
      await api.post(`/projects/${id}/saved-estimates`, saveDraft);
      await loadSavedEstimates();
      setSaveDialogOpen(false);
      toast.success(`Estimate "${saveDraft.name}" saved`);
      if (startNew) {
        await api.post(`/projects/${id}/clear`, {});
        await refresh();
        toast.info("Workspace cleared — ready for next takeoff", { duration: 5000 });
      } else {
        const er = await api.get(`/projects/${id}/estimate`);
        setEstimate(er.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const loadSavedEstimate = async (est) => {
    if ((project.line_items || []).length > 0) {
      if (!window.confirm(`This will replace the current ${project.line_items.length} line items with "${est.name}". Continue?`)) return;
    }
    try {
      await api.post(`/projects/${id}/saved-estimates/${est.id}/load`);
      await refresh();
      toast.success(`Loaded "${est.name}"`);
    } catch {
      toast.error("Load failed");
    }
  };

  const deleteSavedEstimate = async (est) => {
    if (!window.confirm(`Delete saved estimate "${est.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/projects/${id}/saved-estimates/${est.id}`);
      await loadSavedEstimates();
      toast.success("Estimate deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  // Group saved estimates by folder (root first, then alphabetical)
  const savedTree = useMemo(() => {
    const groups = {};
    for (const e of savedEstimates) {
      const f = e.folder || "";
      if (!groups[f]) groups[f] = [];
      groups[f].push(e);
    }
    const folders = Object.keys(groups).sort((a, b) => {
      if (a === "") return -1;
      if (b === "") return 1;
      return a.localeCompare(b);
    });
    return folders.map((f) => ({ folder: f, items: groups[f] }));
  }, [savedEstimates]);

  const existingFolders = useMemo(() => {
    return Array.from(new Set(savedEstimates.map((e) => e.folder || "").filter(Boolean))).sort();
  }, [savedEstimates]);

  const saveItems = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${id}`, { line_items: project.line_items });
      const er = await api.get(`/projects/${id}/estimate`);
      setEstimate(er.data);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const exportPdf = async () => {
    try {
      const r = await api.get(`/projects/${id}/export-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `estimate-${id.slice(-8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  // Build room/category tree from line_items
  const tree = useMemo(() => {
    const items = project?.line_items || [];
    const rooms = {};
    for (const li of items) {
      const room = (li.room || "General").trim() || "General";
      if (!rooms[room]) rooms[room] = { name: room, total: 0, hours: 0, items: [], categories: {} };
      const total = (Number(li.unit_cost) || 0) * (Number(li.quantity) || 0);
      rooms[room].total += total;
      rooms[room].hours += Number(li.labor_hours) || 0;
      rooms[room].items.push(li);
      const cat = li.category || "material";
      if (!rooms[room].categories[cat]) rooms[room].categories[cat] = { total: 0, hours: 0, items: [] };
      rooms[room].categories[cat].total += total;
      rooms[room].categories[cat].hours += Number(li.labor_hours) || 0;
      rooms[room].categories[cat].items.push(li);
    }
    // Sort: General last, others alphabetical
    const ordered = Object.values(rooms).sort((a, b) => {
      if (a.name === "General") return 1;
      if (b.name === "General") return -1;
      return a.name.localeCompare(b.name);
    });
    return ordered;
  }, [project]);

  const toggleRoom = (room) => setCollapsedRooms((p) => ({ ...p, [room]: !p[room] }));
  const toggleCat = (room, cat) => {
    const k = `${room}|${cat}`;
    setCollapsedCats((p) => ({ ...p, [k]: !p[k] }));
  };

  if (!project) {
    return <div className="p-8 text-ink-400 data-label">Loading project…</div>;
  }

  return (
    <div className="min-h-screen" data-testid="project-page">
      {/* Top bar */}
      <div className="border-b border-ink-700 bg-ink-900 sticky top-0 z-10">
        <div className="max-w-[1500px] mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/dashboard" className="text-ink-400 hover:text-ink-50 transition-colors" data-testid="back-to-projects">
              <ArrowLeft size={20} weight="bold" />
            </Link>
            <div className="min-w-0">
              <div className="data-label">Project</div>
              <h1 className="font-heading text-xl tracking-tight font-medium truncate">{project.name}</h1>
            </div>
            {project.quote_date && (
              <div className="hidden md:flex items-center gap-1.5 text-xs text-ink-500 num ml-4">
                <Calendar size={12} weight="duotone" /> {project.quote_date}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={saveItems} disabled={saving} data-testid="quick-save-button" className="text-ink-400 hover:text-ink-50" title="Quick-save line items to this project">
              {saving ? "Saving…" : "Quick save"}
            </Button>
            <Button onClick={openSaveDialog} data-testid="save-estimate-button" className="bg-ink-800 hover:bg-ink-700 text-ink-50 border border-ink-700">
              <FloppyDisk size={16} weight="bold" className="mr-1.5" /> Save Estimate
            </Button>
            <Button onClick={exportPdf} data-testid="export-pdf-button" className="bg-ink-800 hover:bg-ink-700 text-ink-50 border border-ink-700">
              <Download size={16} weight="bold" className="mr-1.5" /> Export PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Drawings panel */}
        <section className="lg:col-span-4 space-y-4" data-testid="drawings-panel">
          <div className="flex items-center justify-between">
            <div className="data-label">Drawings · {project.drawings?.length || 0}</div>
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-ink-700 hover:border-blueprint rounded-md bg-ink-900 p-8 text-center cursor-pointer transition-colors duration-150"
            data-testid="upload-zone"
          >
            <input
              ref={fileInputRef} type="file" accept=".pdf,image/*" multiple hidden
              onChange={(e) => handleUpload(Array.from(e.target.files || []))}
              data-testid="file-input"
            />
            <UploadSimple size={28} weight="duotone" className="mx-auto text-amber mb-3" />
            <div className="font-heading text-base font-medium mb-1">Drop drawings or click</div>
            <div className="text-xs text-ink-500">PDF, PNG, JPG · up to 30MB each</div>
            {uploading && <div className="mt-3 data-label text-amber">Uploading…</div>}
          </div>

          <div className="space-y-2" data-testid="drawings-list">
            {project.drawings.map((d) => {
              const isPdf = d.content_type === "application/pdf";
              const multiPage = isPdf && d.page_count > 1;
              const sel = pageSelections[d.file_id] || new Set();
              const isExpanded = !!expandedFiles[d.file_id];
              return (
                <div key={d.file_id} className="border border-ink-700 bg-ink-900 rounded-md overflow-hidden">
                  <div className="flex">
                    <div className="w-20 h-20 bg-ink-800 flex items-center justify-center shrink-0 border-r border-ink-700">
                      {previews[d.file_id] ? (
                        <img src={previews[d.file_id]} alt={d.original_filename} className="w-full h-full object-cover" />
                      ) : (
                        isPdf ? <FilePdf size={28} weight="duotone" className="text-ink-500" /> : <ImageSquare size={28} weight="duotone" className="text-ink-500" />
                      )}
                    </div>
                    <div className="flex-1 p-3 min-w-0">
                      <div className="text-sm font-medium truncate">{d.original_filename}</div>
                      <div className="text-xs text-ink-500 num mt-0.5">
                        {d.page_count > 1 ? `${d.page_count} pages · ` : ""}{(d.size / 1024).toFixed(0)} KB
                      </div>
                      {multiPage && (
                        <button
                          onClick={() => setExpandedFiles((e) => ({ ...e, [d.file_id]: !isExpanded }))}
                          className="mt-1 inline-flex items-center gap-1 text-xs text-amber hover:text-amber-hover transition-colors"
                          data-testid={`toggle-pages-${d.file_id}`}
                        >
                          {isExpanded ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                          <span className="num">{sel.size} of {d.page_count} pages</span>
                        </button>
                      )}
                    </div>
                    <button onClick={() => removeDrawing(d.file_id)} className="px-3 text-ink-500 hover:text-red-400 transition-colors" data-testid={`remove-drawing-${d.file_id}`}>
                      <X size={16} weight="bold" />
                    </button>
                  </div>

                  {multiPage && isExpanded && (
                    <div className="border-t border-ink-700 bg-ink-950/50 p-3" data-testid={`page-picker-${d.file_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="data-label">Pages to analyze</div>
                        <div className="flex gap-3 text-xs">
                          <button onClick={() => setAllPages(d.file_id, d.page_count, true)} className="text-ink-400 hover:text-amber transition-colors" data-testid={`select-all-${d.file_id}`}>All</button>
                          <button onClick={() => setAllPages(d.file_id, d.page_count, false)} className="text-ink-400 hover:text-amber transition-colors" data-testid={`select-none-${d.file_id}`}>None</button>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {Array.from({ length: d.page_count }, (_, i) => i + 1).map((pn) => (
                          <PageThumb key={pn} fileId={d.file_id} pageNum={pn} selected={sel.has(pn)} onToggle={() => togglePage(d.file_id, pn)} />
                        ))}
                      </div>
                      <div className="mt-2 text-xs text-ink-500 leading-relaxed">
                        Tip: skip cover, title, and text-only pages. Elevations &amp; plans give the best takeoffs.
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <Button
            onClick={openRunDialog}
            disabled={analyzing || !project.drawings?.length || totalSelectedPages === 0}
            data-testid="analyze-button"
            className="w-full h-12 bg-amber hover:bg-amber-hover text-ink-950 font-semibold disabled:opacity-50 transition-colors"
          >
            <Sparkle size={18} weight="duotone" className="mr-2" />
            {analyzing ? "Analyzing drawings…" : `Run AI takeoff${totalSelectedPages ? ` · ${totalSelectedPages} page${totalSelectedPages !== 1 ? "s" : ""}` : ""}`}
          </Button>

          {(analyzing || project.status === "analyzing") && (
            <button
              onClick={async () => {
                try {
                  await api.post(`/projects/${id}/cancel-analyze`);
                  if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
                  setAnalyzing(false);
                  toast.success("Analysis cancelled. You can run again.");
                  await refresh();
                } catch {
                  toast.error("Reset failed");
                }
              }}
              className="w-full text-center text-xs text-ink-500 hover:text-amber transition-colors py-1"
              data-testid="cancel-analyze-button"
            >
              Cancel / reset analysis
            </button>
          )}

          {totalSelectedPages > 0 && !analyzing && (
            <div className="border border-ink-700 bg-ink-900 rounded-md p-3 flex items-center justify-between" data-testid="cost-preview">
              <div className="flex items-center gap-2">
                <CurrencyDollar size={14} weight="duotone" className="text-amber" />
                <div>
                  <div className="data-label">Est. AI cost</div>
                  <div className="text-xs text-ink-500 mt-0.5">~${estimatedCost.low.toFixed(2)}–${estimatedCost.high.toFixed(2)} for {totalSelectedPages} page{totalSelectedPages !== 1 ? "s" : ""}</div>
                </div>
              </div>
              <div className="num text-lg font-semibold text-ink-50">~${estimatedCost.mid.toFixed(2)}</div>
            </div>
          )}

          {project.context && (
            <div className="border border-ink-700 bg-ink-900 rounded-md p-3" data-testid="project-context">
              <div className="data-label mb-2 flex items-center gap-1.5"><NotePencil size={12} /> Project context</div>
              <div className="text-xs text-ink-400 whitespace-pre-wrap leading-relaxed">{project.context}</div>
            </div>
          )}

          {project.ai_summary && (
            <div className="border border-ink-700 bg-ink-900 rounded-md p-4">
              <div className="data-label mb-2 text-amber">AI Summary</div>
              <p className="text-sm text-ink-400 leading-relaxed">{project.ai_summary}</p>
            </div>
          )}

          {/* Saved estimates panel */}
          <div className="border border-ink-700 bg-ink-900 rounded-md overflow-hidden" data-testid="saved-estimates-panel">
            <div className="px-4 py-3 border-b border-ink-700 flex items-center justify-between">
              <div className="data-label flex items-center gap-2">
                <FolderOpen size={12} weight="duotone" className="text-amber" />
                Saved Estimates · {savedEstimates.length}
              </div>
            </div>
            {savedEstimates.length === 0 ? (
              <div className="p-5 text-center">
                <div className="text-xs text-ink-500 leading-relaxed">
                  No saved estimates yet. Click <b className="text-amber">Save Estimate</b> in the top bar to snapshot the current takeoff.
                </div>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                {savedTree.map((group) => {
                  const collapsed = !!folderCollapsed[group.folder];
                  const total = group.items.reduce((s, e) => s + (Number(e.totals?.grand_total) || 0), 0);
                  return (
                    <div key={group.folder || "__root__"} className="border-b border-ink-700/60 last:border-b-0">
                      <button
                        onClick={() => setFolderCollapsed((p) => ({ ...p, [group.folder]: !collapsed }))}
                        className="w-full flex items-center justify-between px-4 py-2 hover:bg-ink-800/40 transition-colors"
                        data-testid={`folder-toggle-${group.folder || "root"}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {collapsed ? <CaretRight size={11} weight="bold" className="text-ink-500" /> : <CaretDown size={11} weight="bold" className="text-amber" />}
                          <FolderSimple size={13} weight="duotone" className="text-amber shrink-0" />
                          <span className="text-xs font-medium tracking-wide truncate">
                            {group.folder ? group.folder.split("/").join(" › ") : "(no folder)"}
                          </span>
                          <span className="text-[10px] num text-ink-500">· {group.items.length}</span>
                        </div>
                        <span className="text-xs num text-ink-400">${total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                      </button>
                      {!collapsed && (
                        <div>
                          {group.items.map((est) => (
                            <div key={est.id} className="pl-9 pr-3 py-2 border-t border-ink-700/40 hover:bg-ink-800/30 transition-colors group flex items-center justify-between gap-2" data-testid={`saved-estimate-${est.id}`}>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm truncate">{est.name}</div>
                                <div className="text-[10px] text-ink-500 num mt-0.5">
                                  {new Date(est.created_at).toLocaleDateString()} · ${(est.totals?.grand_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity shrink-0">
                                <button
                                  onClick={() => loadSavedEstimate(est)}
                                  title="Load into workspace"
                                  className="p-1.5 text-ink-400 hover:text-amber transition-colors"
                                  data-testid={`load-saved-${est.id}`}
                                >
                                  <ArrowSquareOut size={13} weight="bold" />
                                </button>
                                <button
                                  onClick={() => deleteSavedEstimate(est)}
                                  title="Delete"
                                  className="p-1.5 text-ink-400 hover:text-red-400 transition-colors"
                                  data-testid={`delete-saved-${est.id}`}
                                >
                                  <Trash size={13} weight="bold" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* Line items grouped by room */}
        <section className="lg:col-span-8 space-y-4">
          <div className="flex items-center justify-between">
            <div className="data-label flex items-center gap-2">
              <ListBullets size={14} /> Takeoff · {(project.line_items || []).length} items
              {tree.length > 0 && <span className="text-ink-500"> · {tree.length} room{tree.length !== 1 ? "s" : ""}</span>}
            </div>
            <Button variant="ghost" onClick={() => addBlankItem("General")} data-testid="add-line-item" className="text-amber hover:text-amber-hover h-8 text-xs">
              <Plus size={14} weight="bold" className="mr-1" /> Add item
            </Button>
          </div>

          {tree.length === 0 ? (
            <div className="border border-ink-700 bg-ink-900 rounded-md p-12 text-center">
              <Files size={36} weight="duotone" className="mx-auto text-ink-500 mb-3" />
              <div className="text-ink-400 text-sm mb-1">No line items yet</div>
              <div className="text-ink-500 text-xs">Upload drawings, optionally add project context, and click "Run AI takeoff".</div>
            </div>
          ) : (
            <div className="space-y-3">
              {tree.map((room) => {
                const roomCollapsed = !!collapsedRooms[room.name];
                const cats = Object.entries(room.categories).sort((a, b) => CATEGORIES.indexOf(a[0]) - CATEGORIES.indexOf(b[0]));
                return (
                  <div key={room.name} className="border border-ink-700 bg-ink-900 rounded-md overflow-hidden" data-testid={`room-group-${room.name}`}>
                    <button
                      onClick={() => toggleRoom(room.name)}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-ink-800/60 transition-colors group"
                      data-testid={`toggle-room-${room.name}`}
                    >
                      <div className="flex items-center gap-3">
                        {roomCollapsed ? <CaretRight size={16} weight="bold" className="text-ink-500 group-hover:text-amber" /> : <CaretDown size={16} weight="bold" className="text-amber" />}
                        <House size={16} weight="duotone" className="text-amber" />
                        <span className="font-heading text-base font-medium tracking-tight">{room.name}</span>
                        <span className="data-label text-ink-500">{room.items.length} item{room.items.length !== 1 ? "s" : ""}</span>
                      </div>
                      <div className="flex items-baseline gap-4">
                        <span className="text-xs text-ink-500 num">{room.hours.toFixed(1)} hrs</span>
                        <span className="font-mono text-base font-semibold text-ink-50 tabular-nums">${room.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </button>

                    {!roomCollapsed && (
                      <div className="border-t border-ink-700">
                        {cats.map(([catKey, cat]) => {
                          const ck = `${room.name}|${catKey}`;
                          const catCollapsed = !!collapsedCats[ck];
                          return (
                            <div key={catKey} className="border-b border-ink-700/60 last:border-b-0">
                              <button
                                onClick={() => toggleCat(room.name, catKey)}
                                className="w-full flex items-center justify-between px-6 py-2.5 bg-ink-950/30 hover:bg-ink-800/40 transition-colors text-left"
                                data-testid={`toggle-cat-${room.name}-${catKey}`}
                              >
                                <div className="flex items-center gap-2">
                                  {catCollapsed ? <CaretRight size={12} weight="bold" className="text-ink-500" /> : <CaretDown size={12} weight="bold" className="text-ink-400" />}
                                  <span className="text-xs uppercase tracking-[0.18em] font-bold text-ink-400">{CATEGORY_LABEL[catKey] || catKey}</span>
                                  <span className="text-xs text-ink-500 num">· {cat.items.length}</span>
                                </div>
                                <span className="font-mono text-sm tabular-nums text-ink-50">${cat.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </button>
                              {!catCollapsed && (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm" data-testid={`items-table-${room.name}-${catKey}`}>
                                    <thead>
                                      <tr className="text-ink-500 border-b border-ink-700/60">
                                        <th className="text-left px-4 py-1.5 data-label">Item</th>
                                        <th className="text-left px-2 py-1.5 data-label">Room</th>
                                        <th className="text-right px-2 py-1.5 data-label">Qty</th>
                                        <th className="text-left px-1 py-1.5 data-label">Unit</th>
                                        <th className="text-right px-2 py-1.5 data-label">Cost</th>
                                        <th className="text-right px-2 py-1.5 data-label">Hrs</th>
                                        <th className="text-right px-3 py-1.5 data-label">Total</th>
                                        <th></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {cat.items.map((li) => {
                                        const total = (Number(li.unit_cost) || 0) * (Number(li.quantity) || 0);
                                        return (
                                          <tr key={li.id} className="border-b border-ink-700/40 hover:bg-ink-800/40 transition-colors" data-testid={`line-item-row-${li.id}`}>
                                            <td className="px-4 py-1.5 min-w-[260px]">
                                              <input
                                                value={li.name || ""}
                                                onChange={(e) => updateItem(li.id, { name: e.target.value })}
                                                className="w-full bg-transparent font-medium text-ink-50 focus:outline-none"
                                              />
                                              <div className="text-xs text-ink-500 mt-0.5 num">
                                                {[li.width_in && `W${li.width_in}`, li.height_in && `H${li.height_in}`, li.depth_in && `D${li.depth_in}`].filter(Boolean).join(" × ")}
                                                {li.material ? ` · ${li.material}` : ""}
                                                {li.finish ? ` · ${li.finish}` : ""}
                                              </div>
                                            </td>
                                            <td className="px-2 py-1.5">
                                              <input
                                                value={li.room || "General"}
                                                onChange={(e) => updateItem(li.id, { room: e.target.value })}
                                                className="w-28 bg-transparent text-xs text-ink-400 focus:outline-none focus:bg-ink-950 focus:px-1 rounded-sm"
                                                placeholder="General"
                                              />
                                            </td>
                                            <td className="px-2 py-1.5 text-right">
                                              <input type="number" step="0.01" value={li.quantity} onChange={(e) => updateItem(li.id, { quantity: parseFloat(e.target.value) || 0 })} className="w-16 bg-transparent text-right num focus:outline-none focus:bg-ink-950 focus:px-1 rounded-sm" />
                                            </td>
                                            <td className="px-1 py-1.5">
                                              <select value={li.unit} onChange={(e) => updateItem(li.id, { unit: e.target.value })} className="bg-transparent text-xs num focus:outline-none">
                                                {UNITS.map((u) => <option key={u} value={u} className="bg-ink-900">{u}</option>)}
                                              </select>
                                            </td>
                                            <td className="px-2 py-1.5 text-right num">
                                              <input type="number" step="0.01" value={li.unit_cost} onChange={(e) => updateItem(li.id, { unit_cost: parseFloat(e.target.value) || 0 })} className="w-20 bg-transparent text-right focus:outline-none focus:bg-ink-950 focus:px-1 rounded-sm" />
                                            </td>
                                            <td className="px-2 py-1.5 text-right num">
                                              <input type="number" step="0.1" value={li.labor_hours} onChange={(e) => updateItem(li.id, { labor_hours: parseFloat(e.target.value) || 0 })} className="w-14 bg-transparent text-right focus:outline-none focus:bg-ink-950 focus:px-1 rounded-sm" />
                                            </td>
                                            <td className="px-3 py-1.5 text-right num font-semibold text-ink-50">
                                              ${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                            <td className="px-2 py-1.5 text-right">
                                              <button onClick={() => removeItem(li.id)} className="text-ink-500 hover:text-red-400" data-testid={`remove-item-${li.id}`}>
                                                <Trash size={13} weight="bold" />
                                              </button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <div className="px-4 py-2 flex justify-end">
                          <button onClick={() => addBlankItem(room.name)} className="text-xs text-amber hover:text-amber-hover transition-colors flex items-center gap-1" data-testid={`add-item-to-${room.name}`}>
                            <Plus size={11} weight="bold" /> Add item to {room.name}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <EstimateSummary estimate={liveEstimate} dirty={!!estimate && JSON.stringify(estimate) !== JSON.stringify(liveEstimate)} onRefresh={refreshTotals} saving={saving} />
        </section>
      </div>

      {/* Pre-analyze dialog */}
      <Dialog open={runDialogOpen} onOpenChange={setRunDialogOpen}>
        <DialogContent className="bg-ink-900 border-ink-700 text-ink-50 max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Sparkle size={18} weight="duotone" className="text-amber" /> Run AI takeoff
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <Label className="data-label mb-1.5 block">Quote date</Label>
              <Input
                type="date"
                value={quoteDateDraft}
                onChange={(e) => setQuoteDateDraft(e.target.value)}
                className="bg-ink-950 border-ink-700 num"
                data-testid="quote-date-input"
              />
            </div>
            <div>
              <Label className="data-label mb-1.5 block">Project context (helps the AI)</Label>
              <Textarea
                value={contextDraft}
                onChange={(e) => setContextDraft(e.target.value)}
                rows={8}
                className="bg-ink-950 border-ink-700 leading-relaxed"
                placeholder={`Examples:\n• Ceilings 9'2" throughout. 10' in the great room.\n• Kitchen perimeter painted MDF (SW 7005). Island in stained walnut.\n• Master bath: white oak, stained on-site, satin lacquer.\n• Bar in family room: walnut with brass hardware.\n• Drawer slides: Blum Tandem, full-extension, soft-close.\n• Treat the wall labeled "REF" as the kitchen, not the bar.`}
                data-testid="context-input"
              />
              <div className="text-xs text-ink-500 mt-1.5">
                The more context you give, the better the takeoff. Mention room layouts, finishes, hardware standards, ceiling heights, and how to handle ambiguous areas.
              </div>
            </div>

            <div className="border border-ink-700 bg-ink-950 rounded-md p-3 grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="data-label">Pages selected</div>
                <div className="num text-lg mt-1">{totalSelectedPages}</div>
              </div>
              <div className="text-right">
                <div className="data-label">Est. AI cost</div>
                <div className="num text-lg mt-1 text-amber">~${estimatedCost.mid.toFixed(2)}</div>
              </div>
            </div>

            {(project.line_items || []).length > 0 && (
              <div className="border border-ink-700 bg-ink-950 rounded-md p-3" data-testid="mode-toggle">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={appendMode}
                    onChange={(e) => setAppendMode(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-amber"
                    data-testid="append-mode-checkbox"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium">
                      {appendMode ? "Add to existing items" : "Replace all existing items"}
                    </div>
                    <div className="text-xs text-ink-500 mt-1 leading-relaxed">
                      {appendMode
                        ? `New AI-extracted items will be added to the ${project.line_items.length} items already in this project. Use this when running pages in batches.`
                        : `All ${project.line_items.length} current line items will be replaced by the new analysis. Save or export first if you want to keep them.`}
                    </div>
                  </div>
                </label>
              </div>
            )}

            {willExceedCap && (
              <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/40 rounded-md p-3 leading-relaxed" data-testid="cap-warning">
                <div className="font-bold mb-1">⚠ May exceed per-call budget cap ($2.00)</div>
                Your Universal LLM key has a $2 per-call limit. {totalSelectedPages} pages may cost up to ${estimatedCost.high.toFixed(2)} and could be cut off mid-analysis. <b>Recommended:</b> close this dialog, use the page selector to pick 2–3 pages, run, then re-open this dialog with "Add to existing items" checked and pick the next batch.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRunDialogOpen(false)} data-testid="cancel-run-button">Cancel</Button>
            <Button onClick={confirmRunAnalyze} data-testid="confirm-run-button" className="bg-amber hover:bg-amber-hover text-ink-950 font-semibold">
              <Sparkle size={16} weight="duotone" className="mr-1.5" /> Run takeoff
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Estimate dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="bg-ink-900 border-ink-700 text-ink-50 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FloppyDisk size={18} weight="duotone" className="text-amber" /> Save estimate
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="data-label mb-1.5 block">Estimate name</Label>
              <Input
                value={saveDraft.name}
                onChange={(e) => setSaveDraft({ ...saveDraft, name: e.target.value })}
                placeholder="Smith Residence — Kitchen Phase 1"
                className="bg-ink-950 border-ink-700"
                autoFocus
                data-testid="save-estimate-name-input"
              />
            </div>
            <div>
              <Label className="data-label mb-1.5 block">Folder · use / for nested folders</Label>
              <Input
                value={saveDraft.folder}
                onChange={(e) => setSaveDraft({ ...saveDraft, folder: e.target.value })}
                placeholder="e.g. Kitchen / Phase 1   (leave blank for root)"
                className="bg-ink-950 border-ink-700"
                data-testid="save-estimate-folder-input"
                list="existing-folders"
              />
              {existingFolders.length > 0 && (
                <datalist id="existing-folders">
                  {existingFolders.map((f) => <option key={f} value={f} />)}
                </datalist>
              )}
              {existingFolders.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="existing-folders-pills">
                  {existingFolders.slice(0, 6).map((f) => (
                    <button
                      type="button"
                      key={f}
                      onClick={() => setSaveDraft({ ...saveDraft, folder: f })}
                      className="text-[10px] uppercase tracking-wider font-bold border border-ink-700 hover:border-amber text-ink-400 hover:text-amber px-2 py-0.5 rounded-sm transition-colors"
                    >
                      <FolderSimple size={9} weight="bold" className="inline mr-1" />
                      {f}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="data-label mb-1.5 block">Notes (optional)</Label>
              <Textarea
                value={saveDraft.notes}
                onChange={(e) => setSaveDraft({ ...saveDraft, notes: e.target.value })}
                rows={2}
                placeholder="Internal notes, version info, etc."
                className="bg-ink-950 border-ink-700"
                data-testid="save-estimate-notes-input"
              />
            </div>

            <div className="border border-ink-700 bg-ink-950 rounded-md p-3 grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="data-label">Items</div>
                <div className="num text-base mt-1">{(project.line_items || []).length}</div>
              </div>
              <div className="text-right">
                <div className="data-label">Grand Total</div>
                <div className="num text-base mt-1 text-amber">${(liveEstimate?.grand_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
            </div>
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-between gap-2">
            <Button variant="ghost" onClick={() => setSaveDialogOpen(false)} data-testid="cancel-save-estimate">
              Cancel
            </Button>
            <div className="flex gap-2">
              <Button
                onClick={() => performSave(false)}
                disabled={saving}
                data-testid="save-and-keep-button"
                variant="ghost"
                className="border border-ink-700 hover:border-amber text-ink-50"
              >
                {saving ? "Saving…" : "Save"}
              </Button>
              <Button
                onClick={() => performSave(true)}
                disabled={saving}
                data-testid="save-and-new-button"
                className="bg-amber hover:bg-amber-hover text-ink-950 font-semibold"
              >
                <FolderPlus size={15} weight="bold" className="mr-1.5" />
                Save & start new
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PageThumb({ fileId, pageNum, selected, onToggle }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/files/${fileId}/page/${pageNum}`, { responseType: "blob" });
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        setSrc(url);
      } catch {}
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [fileId, pageNum]);
  return (
    <button type="button" onClick={onToggle} data-testid={`page-thumb-${fileId}-${pageNum}`}
      className={`relative aspect-[3/4] rounded-sm border overflow-hidden group transition-all duration-150 ${selected ? "border-amber ring-1 ring-amber" : "border-ink-700 hover:border-ink-500 opacity-60 hover:opacity-100"}`}>
      {src ? <img src={src} alt={`Page ${pageNum}`} className="w-full h-full object-cover bg-white" /> : <div className="w-full h-full flex items-center justify-center bg-ink-800 num text-xs text-ink-500">{pageNum}</div>}
      <div className="absolute top-1 left-1 num text-[10px] font-bold bg-ink-950/80 text-ink-50 rounded-sm px-1 py-0.5">{pageNum}</div>
      {selected && <div className="absolute top-1 right-1 h-4 w-4 rounded-sm bg-amber text-ink-950 flex items-center justify-center"><Check size={10} weight="bold" /></div>}
    </button>
  );
}

function EstimateSummary({ estimate, dirty, onRefresh, saving }) {
  if (!estimate) return null;
  const Row = ({ label, value, accent, bold }) => (
    <div className="flex justify-between items-baseline py-2 border-b border-ink-700/60 last:border-b-0">
      <span className={`text-sm ${accent ? "text-amber font-heading text-base" : "text-ink-400"}`}>{label}</span>
      <span className={`num ${accent ? "text-2xl font-heading text-amber" : bold ? "text-ink-50 text-base font-semibold" : "text-ink-50 text-sm"}`}>
        ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    </div>
  );
  return (
    <div className="border border-ink-700 bg-ink-900 rounded-md p-6" data-testid="estimate-summary">
      <div className="flex items-center justify-between mb-4">
        <div className="data-label flex items-center gap-2">
          Estimate
          {dirty && (
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber bg-amber/10 border border-amber/40 rounded-sm px-1.5 py-0.5" data-testid="dirty-badge">
              Unsaved
            </span>
          )}
        </div>
        <button
          onClick={onRefresh}
          disabled={saving}
          data-testid="refresh-totals-button"
          className="flex items-center gap-1.5 text-xs text-ink-400 hover:text-amber border border-ink-700 hover:border-amber/60 rounded-md px-2.5 h-7 transition-colors disabled:opacity-50"
          title="Save current line items and refetch totals from server"
        >
          <ArrowsClockwise size={12} weight="bold" className={saving ? "animate-spin" : ""} />
          {saving ? "Refreshing…" : "Refresh totals"}
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-1">
        <div>
          <Row label="Materials" value={estimate.materials_cost} />
          <Row label={`Labor · ${estimate.labor_hours} hrs @ $${estimate.labor_rate}/hr`} value={estimate.labor_cost} />
          <Row label={`Overhead (${estimate.overhead_pct}%)`} value={estimate.overhead} />
          <Row label={`Margin (${estimate.margin_pct}%)`} value={estimate.margin} />
        </div>
        <div>
          <Row label="Subtotal" value={estimate.subtotal} bold />
          {estimate.tax_pct > 0 && <Row label={`Tax (${estimate.tax_pct}%)`} value={estimate.tax} />}
          <div className="pt-2 mt-2 border-t border-amber/40">
            <Row label="Grand Total" value={estimate.grand_total} accent />
          </div>
        </div>
      </div>
    </div>
  );
}
